import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Wifi, Check, Loader2, Sparkles, User, Mail, Phone, Smartphone, MapPin,
  FileText, Calendar, Package, Zap, AlertCircle, PartyPopper, Building2, Hash, Search, Home, ChevronsUpDown, Sun, Moon, Gauge, Crown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { fetchCEP, fetchCNPJ } from "@/lib/brasilapi";
import { isValidCPF, isValidCNPJ, documentoTipo } from "@/lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public`;
const LOGO_URL = "https://customer-assets-agu9un31.emergentagent.net/job_sales-tracker-1336/artifacts/xrne8mgy_versao_branca_03.webp";

const cpfMask = (v) => {
  const d = String(v || "").replace(/\D/g, "").slice(0, 14);
  if (d.length <= 11) {
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0,3)}.${d.slice(3)}`;
    if (d.length <= 9) return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6)}`;
    return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9)}`;
  }
  return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8,12)}-${d.slice(12)}`;
};
const phoneMask = (v) => {
  const d = String(v || "").replace(/\D/g, "").slice(0, 11);
  if (d.length <= 10) {
    if (d.length <= 2) return d.length ? `(${d}` : "";
    if (d.length <= 6) return `(${d.slice(0,2)}) ${d.slice(2)}`;
    return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`;
  }
  return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`;
};
const cepMask = (v) => {
  const d = String(v || "").replace(/\D/g, "").slice(0, 8);
  if (d.length <= 5) return d;
  return `${d.slice(0,5)}-${d.slice(5)}`;
};

export default function OnboardingForm() {
  const [planos, setPlanos] = useState([]);
  const [condos, setCondos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState(null);
  const [cepStatus, setCepStatus] = useState({ loading: false, ok: false, msg: "" });
  // 'undecided' | 'condo' | 'individual' — controla exibição do bloco de endereço
  const [tipoMoradia, setTipoMoradia] = useState("undecided");
  const [condoPickerOpen, setCondoPickerOpen] = useState(false);
  // Status da validação do documento (CPF DV / CNPJ Receita)
  const [docStatus, setDocStatus] = useState({ loading: false, ok: false, invalid: false, msg: "" });
  const complementoRef = useRef(null);
  const vendedor = new URLSearchParams(window.location.search).get("v") || "";

  // Tema (modo escuro/claro) — persiste em localStorage separado do dashboard
  // pra o cliente escolher independente da equipe FLUA.
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem("flua-onboard-theme") || "dark"; }
    catch { return "dark"; }
  });
  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    try { localStorage.setItem("flua-onboard-theme", theme); } catch { /* ignore */ }
  }, [theme]);

  const [form, setForm] = useState({
    nome_completo: "", razao_social: "", cpf: "",
    cep: "", endereco: "", numero: "", complemento: "",
    condominio: "", email: "",
    whatsapp: "", telefone_fixo: "",
    dia_vencimento: 0, plano_slug: "", app_bonus: "",
  });
  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/onboarding/planos`).then((r) => r.data?.planos || []),
      axios.get(`${API}/onboarding/condominios`).then((r) => r.data?.condominios || []).catch(() => []),
    ])
      .then(([p, c]) => { setPlanos(p); setCondos(c); })
      .catch(() => setError("Não conseguimos carregar os planos. Tente recarregar a página."))
      .finally(() => setLoading(false));
  }, []);

  // Auto-lookup de CEP via BrasilAPI (só quando o cliente digita o próprio CEP).
  useEffect(() => {
    if (tipoMoradia !== "individual") return; // não interfere no fluxo do condomínio
    const d = String(form.cep || "").replace(/\D/g, "");
    if (d.length !== 8) {
      if (cepStatus.ok) setCepStatus({ loading: false, ok: false, msg: "" });
      return;
    }
    let cancelled = false;
    setCepStatus({ loading: true, ok: false, msg: "" });
    const t = setTimeout(async () => {
      try {
        const info = await fetchCEP(d);
        if (cancelled) return;
        setForm((prev) => ({
          ...prev,
          endereco: prev.endereco?.trim() ? prev.endereco : info.logradouro,
        }));
        setCepStatus({ loading: false, ok: true, msg: info.resumo || "Endereço encontrado" });
      } catch (e) {
        if (!cancelled) setCepStatus({ loading: false, ok: false, msg: e.message || "CEP não encontrado" });
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line
  }, [form.cep, tipoMoradia]);

  // Validação de CPF (DV local) / CNPJ (DV local + consulta Receita via BrasilAPI).
  useEffect(() => {
    const digits = String(form.cpf || "").replace(/\D/g, "");
    if (digits.length === 0) {
      setDocStatus({ loading: false, ok: false, invalid: false, msg: "" });
      return;
    }
    if (digits.length !== 11 && digits.length !== 14) {
      setDocStatus({ loading: false, ok: false, invalid: false, msg: "" });
      return;
    }
    if (digits.length === 11) {
      // CPF: validação local dos dígitos verificadores.
      if (isValidCPF(digits)) {
        setDocStatus({ loading: false, ok: true, invalid: false, msg: "CPF válido" });
      } else {
        setDocStatus({ loading: false, ok: false, invalid: true, msg: "CPF inválido — confira os dígitos" });
      }
      return;
    }
    // CNPJ: primeiro valida DV local, depois consulta a Receita via BrasilAPI.
    if (!isValidCNPJ(digits)) {
      setDocStatus({ loading: false, ok: false, invalid: true, msg: "CNPJ inválido — confira os dígitos" });
      return;
    }
    let cancelled = false;
    setDocStatus({ loading: true, ok: false, invalid: false, msg: "Consultando Receita Federal…" });
    const t = setTimeout(async () => {
      try {
        const info = await fetchCNPJ(digits);
        if (cancelled) return;
        const nome = info.razao_social || info.nome_fantasia || "";
        const situacao = info.situacao ? ` · ${info.situacao}` : "";
        setDocStatus({
          loading: false, ok: true, invalid: false,
          msg: nome ? `${nome}${situacao}` : "CNPJ ativo na Receita",
        });
        // Auto-preenche a razão social se estiver vazia
        setForm((prev) => (
          !prev.nome_completo.trim() && nome
            ? { ...prev, nome_completo: nome }
            : prev
        ));
      } catch (e) {
        if (!cancelled) setDocStatus({
          loading: false, ok: false, invalid: true,
          msg: e.message || "CNPJ não encontrado na Receita",
        });
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.cpf]);

  const selectedCondo = condos.find((c) => c.nome === form.condominio);

  const escolherCondo = (condo) => {
    setForm((p) => ({
      ...p,
      condominio: condo.nome,
      cep: condo.cep || "",
      endereco: condo.logradouro || "",
      numero: condo.numero || "",
      complemento: "",  // cliente vai preencher com o apto/bloco
    }));
    setTipoMoradia("condo");
    setCondoPickerOpen(false);
    setCepStatus({ loading: false, ok: !!condo.cep, msg: condo.cep ? "Endereço carregado do condomínio" : "" });
    // Foca no complemento pro cliente digitar o número do apartamento
    setTimeout(() => complementoRef.current?.focus(), 100);
  };

  const escolherIndividual = () => {
    setForm((p) => ({ ...p, condominio: "", cep: "", endereco: "", numero: "", complemento: "" }));
    setCepStatus({ loading: false, ok: false, msg: "" });
    setTipoMoradia("individual");
  };

  const trocarMoradia = () => {
    setTipoMoradia("undecided");
    setForm((p) => ({ ...p, condominio: "", cep: "", endereco: "", numero: "", complemento: "" }));
    setCepStatus({ loading: false, ok: false, msg: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (tipoMoradia === "undecided") {
      setError("Selecione se você mora em um dos condomínios listados ou marque 'Não moro em condomínio'.");
      return;
    }
    if (docStatus.invalid || !docStatus.ok) {
      setError(docStatus.msg || "Informe um CPF ou CNPJ válido");
      return;
    }
    if (!form.plano_slug) {
      setError("Escolha um plano para continuar");
      return;
    }
    if (!form.app_bonus) {
      setError("Escolha um app bônus do plano selecionado para continuar");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/onboarding`, { ...form, vendedor });
      setDone(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Não foi possível enviar. Verifique os campos.");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-background text-foreground grid place-items-center p-4">
        <div className="max-w-md w-full rounded-2xl border border-emerald-500/40 bg-emerald-500/5 p-8 text-center space-y-4">
          <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/20 grid place-items-center">
            <PartyPopper className="w-8 h-8 text-emerald-500" />
          </div>
          <h1 className="font-heading font-bold text-2xl">Cadastro recebido!</h1>
          <p className="text-muted-foreground text-sm">{done.mensagem}</p>
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-left">
            <p className="font-semibold text-amber-500 mb-1">O que acontece agora?</p>
            <ol className="space-y-1 text-muted-foreground list-decimal ml-4">
              <li>Seu vendedor valida seus dados no sistema.</li>
              <li>Em até 30 minutos você recebe o link de assinatura no WhatsApp.</li>
              <li>Após assinar, agendamos a instalação do seu plano.</li>
            </ol>
          </div>
          <Badge variant="outline" className="text-emerald-500 border-emerald-500/40">
            Protocolo: {String(done.cliente_id || "").slice(0, 8).toUpperCase()}
          </Badge>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="onboarding-form">
      <div className="border-b border-border/60 bg-gradient-to-br from-primary/15 via-primary/5 to-transparent relative overflow-hidden">
        <div className="absolute inset-0 opacity-40 pointer-events-none" aria-hidden style={{
          backgroundImage:
            "radial-gradient(ellipse 900px 400px at 10% 20%, hsl(var(--primary) / 0.30), transparent 60%), " +
            "radial-gradient(ellipse 700px 500px at 90% 0%, hsl(var(--primary) / 0.20), transparent 60%)",
        }} />
        <div className="absolute inset-x-0 bottom-0 h-20 flua-waves opacity-80 pointer-events-none" aria-hidden />
        <div className="relative max-w-3xl mx-auto px-4 py-5 flex items-center justify-between flex-wrap gap-3">
          <img src={LOGO_URL} alt="FLUA" className="h-8" />
          <div className="flex items-center gap-2">
            {vendedor && (
              <Badge variant="outline" className="text-xs backdrop-blur-sm bg-background/40">
                <User className="w-3 h-3 mr-1" strokeWidth={1.5} /> Atendimento: {vendedor}
              </Badge>
            )}
            <button
              type="button"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              data-testid="onb-theme-toggle"
              aria-label="Alternar tema claro/escuro"
              className="w-9 h-9 rounded-full border border-border bg-background/60 backdrop-blur-md grid place-items-center text-foreground hover:border-primary hover:text-primary transition-colors"
            >
              {theme === "dark" ? <Sun className="w-4 h-4" strokeWidth={1.5} /> : <Moon className="w-4 h-4" strokeWidth={1.5} />}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight flex items-center gap-2">
            <Wifi className="w-7 h-7 text-primary" strokeWidth={1.5} /> Bem-vindo à <span className="font-light italic text-primary">FLUA</span>
          </h1>
          <p className="text-muted-foreground mt-2 font-light">Preencha seus dados abaixo e escolha o melhor plano para você. Nossa equipe entra em contato para agendar a instalação.</p>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground py-10 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Carregando…
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Bloco: Dados pessoais */}
            <section className="rounded-2xl border border-border bg-card/80 backdrop-blur-sm p-5 sm:p-6 space-y-4 card-flua">
              <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
                <User className="w-4 h-4 text-primary" strokeWidth={1.5} /> Seus dados
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2 space-y-1.5">
                  <Label>Nome completo / Razão social *</Label>
                  <Input value={form.nome_completo} onChange={(e) => setF("nome_completo", e.target.value)} data-testid="onb-nome" required />
                </div>
                <div className="sm:col-span-2 space-y-1.5">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <Label className="flex items-center gap-1.5"><FileText className="w-3.5 h-3.5 text-muted-foreground" /> CPF ou CNPJ *</Label>
                    {form.cpf && (
                      <Badge
                        variant="outline"
                        className={`text-[10px] py-0 h-5 ${
                          docStatus.loading ? "text-sky-500 border-sky-500/40" :
                          docStatus.ok ? "text-emerald-500 border-emerald-500/40" :
                          docStatus.invalid ? "text-red-500 border-red-500/40" :
                          "text-muted-foreground border-border"
                        }`}
                        data-testid="onb-doc-badge"
                      >
                        {docStatus.loading && <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Consultando…</>}
                        {!docStatus.loading && docStatus.ok && <><Check className="w-3 h-3 mr-1" /> {documentoTipo(form.cpf)} válido</>}
                        {!docStatus.loading && docStatus.invalid && <><AlertCircle className="w-3 h-3 mr-1" /> {documentoTipo(form.cpf)} inválido</>}
                        {!docStatus.loading && !docStatus.ok && !docStatus.invalid && <>{documentoTipo(form.cpf)}</>}
                      </Badge>
                    )}
                  </div>
                  <Input
                    value={form.cpf}
                    onChange={(e) => setF("cpf", cpfMask(e.target.value))}
                    placeholder="000.000.000-00"
                    data-testid="onb-cpf"
                    inputMode="numeric"
                    maxLength={18}
                    className={docStatus.invalid ? "border-red-500/50 focus-visible:ring-red-500/40" : ""}
                    required
                  />
                  {docStatus.msg && !docStatus.loading && (
                    <p
                      className={`text-[11px] flex items-center gap-1 ${
                        docStatus.ok ? "text-emerald-500" : docStatus.invalid ? "text-red-500" : "text-muted-foreground"
                      }`}
                      data-testid="onb-doc-msg"
                    >
                      {docStatus.ok ? <Check className="w-3 h-3" /> : docStatus.invalid ? <AlertCircle className="w-3 h-3" /> : null}
                      {docStatus.msg}
                    </p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-muted-foreground" /> E-mail *</Label>
                  <Input type="email" value={form.email} onChange={(e) => setF("email", e.target.value)} data-testid="onb-email" required />
                </div>
                <div className="space-y-1.5">
                  <Label className="flex items-center gap-1.5"><Smartphone className="w-3.5 h-3.5 text-emerald-500" /> Celular WhatsApp *</Label>
                  <Input value={form.whatsapp} onChange={(e) => setF("whatsapp", phoneMask(e.target.value))} placeholder="(11) 90000-0000" inputMode="numeric" data-testid="onb-whatsapp" required />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-muted-foreground" /> Telefone fixo (opcional)</Label>
                  <Input value={form.telefone_fixo} onChange={(e) => setF("telefone_fixo", phoneMask(e.target.value))} placeholder="(11) 3000-0000" inputMode="numeric" data-testid="onb-fixo" />
                </div>
              </div>
            </section>

            {/* Bloco: Onde você mora? (Condomínio-first) */}
            <section
              className="rounded-xl border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-transparent p-5 space-y-4"
              data-testid="onb-moradia-section"
            >
              <div>
                <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
                  <Home className="w-4 h-4 text-primary" /> Onde você mora?
                </h2>
                <p className="text-xs text-muted-foreground mt-1">
                  Se você mora em um dos condomínios abaixo, <strong className="text-primary">selecione o seu</strong> e a gente já traz o endereço pronto. Você só precisa informar o número do apartamento.
                </p>
              </div>

              {/* Combobox de condomínios com busca */}
              {condos.length > 0 && tipoMoradia !== "individual" && tipoMoradia !== "condo" && (
                <div className="space-y-2" data-testid="onb-condo-combobox">
                  <Popover open={condoPickerOpen} onOpenChange={setCondoPickerOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        role="combobox"
                        aria-expanded={condoPickerOpen}
                        className="w-full flex items-center justify-between gap-2 rounded-lg border-2 border-primary/40 bg-background px-4 py-3 text-left hover:border-primary hover:bg-primary/5 transition-all"
                        data-testid="onb-condo-picker-trigger"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <Building2 className="w-4 h-4 text-primary shrink-0" />
                          <span className="text-sm font-medium truncate">
                            Buscar meu condomínio…
                          </span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge variant="outline" className="text-[10px] tabular-nums hidden sm:inline-flex">
                            {condos.length} disponíveis
                          </Badge>
                          <ChevronsUpDown className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-[min(92vw,32rem)] p-0"
                      align="start"
                      data-testid="onb-condo-picker-content"
                    >
                      <Command>
                        <CommandInput
                          placeholder="Digite o nome, rua ou bairro…"
                          data-testid="onb-condo-search"
                        />
                        <CommandList>
                          <CommandEmpty>Nenhum condomínio encontrado.</CommandEmpty>
                          <CommandGroup>
                            {condos.map((c) => (
                              <CommandItem
                                key={c.nome}
                                value={`${c.nome} ${c.endereco_completo || ""}`}
                                onSelect={() => escolherCondo(c)}
                                data-testid={`onb-condo-opt-${c.nome}`}
                                className="cursor-pointer"
                              >
                                <Building2 className="w-4 h-4 mr-2 text-muted-foreground shrink-0" />
                                <div className="min-w-0 flex-1">
                                  <div className="font-medium truncate text-sm">{c.nome}</div>
                                  {c.endereco_completo && (
                                    <div className="text-[11px] text-muted-foreground truncate">
                                      {c.endereco_completo}
                                    </div>
                                  )}
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {/* Botão "Não moro em condomínio" — visível como CTA secundária */}
              {tipoMoradia !== "individual" && tipoMoradia !== "condo" && (
                <div className="relative flex items-center gap-3 py-1" data-testid="onb-separator">
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">ou</span>
                  <div className="flex-1 h-px bg-border" />
                </div>
              )}
              {tipoMoradia !== "individual" && tipoMoradia !== "condo" && (
                <button
                  type="button"
                  onClick={escolherIndividual}
                  className="w-full rounded-lg border-2 border-primary/50 bg-primary/5 hover:bg-primary/10 hover:border-primary p-4 text-sm font-semibold text-primary transition-all flex items-center justify-center gap-2 group"
                  data-testid="onb-nao-moro-condo"
                >
                  <Home className="w-4 h-4 group-hover:scale-110 transition-transform" />
                  <span>Não moro em condomínio — preencher endereço manualmente</span>
                </button>
              )}

              {/* Modo Condomínio selecionado — pede só o complemento (apto) */}
              {tipoMoradia === "condo" && selectedCondo && (
                <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-4 space-y-3" data-testid="onb-condo-selected">
                  <div className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold">{selectedCondo.nome}</div>
                      {selectedCondo.endereco_completo && (
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          {selectedCondo.endereco_completo}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={trocarMoradia}
                      className="text-[11px] text-primary hover:underline shrink-0"
                      data-testid="onb-trocar-condo"
                    >
                      Trocar
                    </button>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="flex items-center gap-1.5 text-sm">
                      <Hash className="w-3.5 h-3.5 text-primary" />
                      Qual seu apartamento / bloco / unidade? *
                    </Label>
                    <Input
                      ref={complementoRef}
                      value={form.complemento}
                      onChange={(e) => setF("complemento", e.target.value)}
                      placeholder="Ex: Bloco B · Apto 405"
                      data-testid="onb-complemento-condo"
                      autoFocus
                      required
                    />
                    <p className="text-[10px] text-muted-foreground">Único campo que você precisa preencher — o resto já veio do cadastro do condomínio.</p>
                  </div>
                </div>
              )}
            </section>

            {/* Bloco: Endereço manual (só aparece quando "não moro em condomínio") */}
            {tipoMoradia === "individual" && (
              <section className="rounded-2xl border border-border bg-card/80 backdrop-blur-sm p-5 sm:p-6 space-y-4 card-flua" data-testid="onb-endereco-manual">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-primary" /> Endereço da instalação
                  </h2>
                  <button
                    type="button"
                    onClick={trocarMoradia}
                    className="text-[11px] text-primary hover:underline"
                    data-testid="onb-voltar-condos"
                  >
                    Voltar aos condomínios
                  </button>
                </div>
                <p className="text-[11px] text-muted-foreground">Comece pelo CEP — preenchemos o resto pra você.</p>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-1.5 sm:col-span-1">
                    <Label className="flex items-center gap-1.5"><Search className="w-3.5 h-3.5 text-primary" /> CEP *</Label>
                    <Input
                      value={form.cep}
                      onChange={(e) => setF("cep", cepMask(e.target.value))}
                      placeholder="00000-000"
                      inputMode="numeric"
                      maxLength={9}
                      data-testid="onb-cep"
                      required
                    />
                    {cepStatus.loading && (
                      <p className="text-[11px] text-sky-500 flex items-center gap-1" data-testid="cep-loading">
                        <Loader2 className="w-3 h-3 animate-spin" /> Consultando BrasilAPI…
                      </p>
                    )}
                    {!cepStatus.loading && cepStatus.ok && (
                      <p className="text-[11px] text-emerald-500 flex items-center gap-1" data-testid="cep-ok">
                        <Check className="w-3 h-3" /> {cepStatus.msg}
                      </p>
                    )}
                    {!cepStatus.loading && !cepStatus.ok && cepStatus.msg && (
                      <p className="text-[11px] text-amber-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> {cepStatus.msg} — preencha manualmente
                      </p>
                    )}
                  </div>

                  <div className="space-y-1.5 sm:col-span-2">
                    <Label>Rua / Avenida *</Label>
                    <Input
                      value={form.endereco}
                      onChange={(e) => setF("endereco", e.target.value)}
                      placeholder="Preenchido automaticamente pelo CEP"
                      data-testid="onb-endereco"
                      required
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="flex items-center gap-1.5"><Hash className="w-3.5 h-3.5 text-muted-foreground" /> Número *</Label>
                    <Input
                      value={form.numero}
                      onChange={(e) => setF("numero", e.target.value)}
                      placeholder="Ex: 123"
                      data-testid="onb-numero"
                      inputMode="numeric"
                      required
                    />
                  </div>

                  <div className="space-y-1.5 sm:col-span-2">
                    <Label>Complemento (opcional)</Label>
                    <Input
                      value={form.complemento}
                      onChange={(e) => setF("complemento", e.target.value)}
                      data-testid="onb-complemento"
                      placeholder="Casa dos fundos · Apto 12 · etc"
                    />
                  </div>
                </div>
              </section>
            )}

            {/* Bloco: Vencimento */}
            <section className="rounded-2xl border border-border bg-card/80 backdrop-blur-sm p-5 sm:p-6 space-y-3 card-flua">
              <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
                <Calendar className="w-4 h-4 text-primary" /> Melhor dia para pagamento
              </h2>
              <p className="text-xs text-muted-foreground">Escolha o dia do mês em que a fatura vencerá.</p>
              <div className="grid grid-cols-2 gap-3">
                {[5, 10].map((d) => (
                  <button key={d} type="button"
                    onClick={() => setF("dia_vencimento", d)}
                    data-testid={`onb-vencimento-${d}`}
                    className={`rounded-lg border-2 p-4 text-center transition-all ${
                      form.dia_vencimento === d
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-primary/40"
                    }`}>
                    <div className="text-3xl font-heading font-extrabold tabular-nums">{d}</div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">de cada mês</div>
                  </button>
                ))}
              </div>
            </section>

            {/* Bloco: Escolha do plano */}
            <section className="rounded-2xl border border-border bg-card/80 backdrop-blur-sm p-5 sm:p-6 space-y-4 card-flua">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
                  <Package className="w-4 h-4 text-primary" strokeWidth={1.5} /> Escolha seu plano e um app bônus
                </h2>
                <Badge variant="outline" className="text-[10px] bg-primary/10 text-primary border-primary/40">Obrigatório escolher 1 app</Badge>
              </div>
              <p className="text-xs text-muted-foreground -mt-2">
                Cada plano vem com apps para você escolher. Toque em <strong>Selecionar</strong> no app desejado — o plano é escolhido automaticamente.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                {planos.map((p, idx) => {
                  const selected = form.plano_slug === p.slug;
                  const chosenApp = selected ? p.apps.find((x) => x.slug === form.app_bonus) : null;
                  const extra = Number(chosenApp?.preco_adicional || 0);
                  const total = Number(p.price || 0) + extra;
                  const isTopPlan = idx === planos.length - 1; // último = premium/mais caro
                  return (
                    <div key={p.slug} data-testid={`onb-plano-${p.slug}`}
                      className={`relative rounded-2xl border-2 p-4 sm:p-5 transition-all overflow-hidden ${
                        selected
                          ? "border-primary bg-gradient-to-br from-primary/10 via-card to-card shadow-[0_18px_60px_-20px_hsl(var(--primary)/0.6)]"
                          : "border-border bg-card hover:border-primary/50 hover:shadow-[0_12px_40px_-24px_hsl(var(--primary)/0.5)]"
                      }`}>
                      {isTopPlan && (
                        <div className="absolute top-3 right-3 flex items-center gap-1 text-[9px] uppercase tracking-widest font-semibold text-amber-400 bg-amber-400/10 border border-amber-400/40 rounded-full px-2 py-0.5">
                          <Crown className="w-2.5 h-2.5" strokeWidth={2} /> Top
                        </div>
                      )}
                      <div className="font-heading font-bold text-base sm:text-lg tracking-tight">{p.nome}</div>

                      {/* MEGA em destaque — protagonista do card */}
                      <div className="mt-3 mb-4 rounded-xl border border-primary/25 bg-gradient-to-br from-primary/15 via-primary/5 to-transparent p-3 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/20 grid place-items-center shrink-0">
                          <Gauge className="w-5 h-5 text-primary" strokeWidth={1.75} />
                        </div>
                        <div className="min-w-0">
                          <div className="text-[9px] uppercase tracking-[0.22em] text-primary/80 font-semibold">Velocidade</div>
                          <div className="flex items-baseline gap-1.5">
                            <span className="font-heading font-black text-3xl sm:text-4xl tabular-nums leading-none bg-gradient-to-br from-primary via-primary to-primary/70 bg-clip-text text-transparent">
                              {p.mega}
                            </span>
                            <span className="text-sm font-semibold text-primary">Mega</span>
                          </div>
                        </div>
                        <div className="ml-auto flex flex-col items-end text-right shrink-0">
                          <span className="text-[9px] uppercase tracking-widest text-muted-foreground">Mensal</span>
                          <span className="text-lg font-heading font-extrabold tabular-nums text-emerald-500 leading-none">
                            R$ {p.price.toFixed(2).replace(".", ",")}
                          </span>
                        </div>
                      </div>

                      <ul className="mb-3 grid grid-cols-1 gap-1">
                        {p.beneficios.map((b) => (
                          <li key={b} className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                            <Check className="w-3 h-3 text-emerald-500 shrink-0" strokeWidth={2.5} /> {b}
                          </li>
                        ))}
                      </ul>

                      {/* Apps do plano — obrigatório escolher 1 */}
                      <div className="pt-3 border-t border-border/70 space-y-2">
                        <div className="flex items-center gap-1.5">
                          <Sparkles className="w-3.5 h-3.5 text-primary" strokeWidth={1.5} />
                          <span className="text-[10px] uppercase tracking-[0.22em] text-primary font-semibold">Escolha um app</span>
                        </div>
                        {p.apps.length === 0 ? (
                          <p className="text-[11px] text-muted-foreground italic">Nenhum app disponível para este plano.</p>
                        ) : (
                          <div className="space-y-1.5">
                            {p.apps.map((a) => {
                              const sel = selected && form.app_bonus === a.slug;
                              const preco = Number(a.preco_adicional || 0);
                              return (
                                <div key={a.slug}
                                  data-testid={`onb-app-row-${p.slug}-${a.slug}`}
                                  className={`flex items-center gap-2 rounded-lg border p-2 transition-all ${
                                    sel ? "border-primary bg-primary/10" : "border-border/70 bg-background/40 hover:border-primary/30"
                                  }`}>
                                  {a.icone_url ? (
                                    <img src={a.icone_url} alt="" loading="lazy"
                                      className="w-9 h-9 rounded-lg object-contain bg-background/60 border border-border/60 shrink-0" />
                                  ) : (
                                    <span className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 grid place-items-center shrink-0">
                                      <Sparkles className="w-4 h-4 text-primary" strokeWidth={1.5} />
                                    </span>
                                  )}
                                  <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium truncate">{a.label}</div>
                                    {preco > 0 ? (
                                      <div className="text-[10px] text-emerald-500 font-semibold tabular-nums">+R$ {preco.toFixed(2).replace(".", ",")}/mês</div>
                                    ) : (
                                      <div className="text-[10px] text-muted-foreground">Incluso</div>
                                    )}
                                  </div>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant={sel ? "default" : "outline"}
                                    onClick={() => { setF("plano_slug", p.slug); setF("app_bonus", a.slug); }}
                                    data-testid={`onb-app-select-${p.slug}-${a.slug}`}
                                    className="shrink-0 h-8 text-xs px-3"
                                  >
                                    {sel ? (<><Check className="w-3 h-3 mr-1" strokeWidth={2.5} /> Selecionado</>) : "Selecionar"}
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {selected && form.app_bonus && extra > 0 && (
                        <div className="mt-3 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-2.5 flex items-center justify-between gap-2" data-testid={`onb-app-total-${p.slug}`}>
                          <div className="text-[11px] text-muted-foreground leading-tight">
                            <strong className="text-foreground">{p.nome}</strong> + <strong className="text-foreground">{chosenApp.label}</strong>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-[9px] text-muted-foreground uppercase tracking-widest">Total mensal</div>
                            <div className="font-heading text-sm font-extrabold tabular-nums text-emerald-500">
                              R$ {total.toFixed(2).replace(".", ",")}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            {error && (
              <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-500 flex items-center gap-2" data-testid="onb-error">
                <AlertCircle className="w-4 h-4 shrink-0" /> {error}
              </div>
            )}

            <Button type="submit" size="lg" className="w-full" disabled={submitting} data-testid="onb-submit">
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando…</> : "Finalizar cadastro"}
            </Button>
            <p className="text-[10px] text-muted-foreground text-center">
              Ao enviar, você autoriza a FLUA a entrar em contato pelo WhatsApp/e-mail para agendar a instalação.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
